import type { EventType, ProjectState } from "@livcg/schema";
import { stoppageMinute } from "@livcg/schema";
import { useRef } from "react";
import { readLiveClock } from "../../shared/liveClock.js";
import { sendRaw } from "../../shared/sendRaw.js";
import { cardSteps, chainKey, effectiveChainKey, goalSteps, subsSteps } from "../airSteps.js";

export type ChainPlayer = {
  id: string;
  firstName: string;
  lastName: string;
  number: number | null;
};
export type ChainTeam = { id: string };

type ChainType = Exclude<EventType, "substitution">;

const isGoal = (t: ChainType): t is "goal" | "penalty_goal" | "own_goal" =>
  t === "goal" || t === "penalty_goal" || t === "own_goal";

/** QuickEvents: GOAL/CARD/SUBSTITUTION write the event and ask the server to run
 *  the banner chain. The chain, its timing and the return live on the server, so
 *  every operator sees the same on-air button and a closed browser strands nothing. */
export function useQuickEventChain(slug: string, state: ProjectState) {
  // Between overlay.play and the replicant update a second press must still toggle off,
  // otherwise it writes a duplicate event.
  const pending = useRef<{ key: string; at: number } | null>(null);
  const activeChain = effectiveChainKey(
    state.overlay.sequence?.key ?? null,
    pending.current,
    Date.now(),
  );

  /** Pressing the on-air button again closes the banner early; the event stays. */
  const toggleOff = (type: ChainType, side: "home" | "away", playerId: string) => {
    // Read fresh: a quick second press runs before any re-render.
    const current = effectiveChainKey(
      state.overlay.sequence?.key ?? null,
      pending.current,
      Date.now(),
    );
    if (current !== chainKey(type, side, playerId)) return false;
    pending.current = null;
    void sendRaw("overlay.stop", { slug });
    return true;
  };

  const fire = (type: ChainType, side: "home" | "away", player: ChainPlayer, team: ChainTeam) => {
    if (toggleOff(type, side, player.id)) return;
    // The time is read live at the press: `state` skips clock ticks.
    const clock = readLiveClock(slug);
    const added = stoppageMinute(clock.phase, clock.minute, clock.second);
    void sendRaw("event.add", {
      slug,
      type,
      teamId: team.id,
      playerId: player.id,
      minute: clock.minute,
      second: clock.second,
      ...(added ? { data: { added } } : {}),
    });
    const steps = isGoal(type)
      ? goalSteps(type, side, player, clock.minute, clock.second, added)
      : cardSteps(type, side, player);
    const key = chainKey(type, side, player.id);
    pending.current = { key, at: Date.now() };
    void sendRaw("overlay.play", { slug, key, steps });
  };

  /** Group substitution: 1–5 pairs to the log and one swap banner listing them. */
  const fireSubGroup = (
    side: "home" | "away",
    pairs: {
      outId: string;
      outName: string;
      outNumber: number | null;
      inId: string;
      inName: string;
      inNumber: number | null;
    }[],
    team: ChainTeam,
  ) => {
    if (pairs.length === 0) return;
    const clock = readLiveClock(slug);
    const added = stoppageMinute(clock.phase, clock.minute, clock.second);
    for (const p of pairs) {
      void sendRaw("event.add", {
        slug,
        type: "substitution",
        teamId: team.id,
        playerId: p.outId,
        assistPlayerId: p.inId,
        minute: clock.minute,
        second: clock.second,
        ...(added ? { data: { added } } : {}),
      });
    }
    const swaps = pairs.map((p) => ({
      outNumber: p.outNumber ?? 0,
      outName: p.outName,
      inNumber: p.inNumber ?? 0,
      inName: p.inName,
    }));
    void sendRaw("overlay.play", {
      slug,
      key: `subs:${side}`,
      steps: subsSteps(side, swaps, state.overlay.mode),
    });
  };

  return { activeChain, fire, fireSubGroup, toggleOff };
}
